/*
 * Dual-event page context: page type from the pathname, category/subcategory
 * from the PLP URL, and whatever product the current page registers (the PDP
 * sets SKU + categories once its product loads). Shared by bag, commerce and
 * behaviour helpers — not FX bucketing (see optimizely-fx-page-context).
 */
const LOCALE_PREFIX = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i
const PDP_PATH = /^\/p\/[^/]+\/([^/.]+)(?:\.html)?\/?$/i
const PLP_PATH = /^\/[gd]\/(.*)$/i

let registered = null // {pathname, SKU, Category, Subcategory, page_type}

export const stripLocale = (pathname) => String(pathname || '/').replace(LOCALE_PREFIX, '') || '/'

/** Page type vocabulary shared with the navigation event and FX audience. */
export const classifyPage = (pathname) => {
    const path = stripLocale(pathname).replace(/\/+$/, '') || '/'
    if (path === '/') return 'home'
    if (path.startsWith('/checkout/confirmation')) return 'order_confirm'
    if (path.startsWith('/checkout')) return 'checkout'
    if (path === '/cart') return 'cart'
    if (path === '/search') return 'search'
    if (PDP_PATH.test(path)) return 'pdp'
    if (PLP_PATH.test(path)) return 'plp'
    if (path.startsWith('/account') || path.startsWith('/my-rewards')) return 'account'
    if (path.startsWith('/wishlist') || path.startsWith('/share-wishlist')) return 'wishlist'
    return 'other'
}

/**
 * Category / Subcategory from a product's `c_categories` (leaf first), the
 * way the GTM layer reads it: reversed, 1-char root dropped, lower-cased.
 * @param {string[]} [list]
 * @returns {{Category?: string, Subcategory?: string}}
 */
export const categoriesFromList = (list) => {
    if (!Array.isArray(list) || !list.length) return {}
    const ordered = [...list].map((c) => String(c)).reverse()
    if (ordered[0].length <= 1) ordered.shift()
    const out = {}
    if (ordered[0]) out.Category = ordered[0].toLowerCase()
    if (ordered[1]) out.Subcategory = ordered[1].toLowerCase()
    return out
}

/**
 * Let a page describe its product. Keyed by pathname so a stale registration
 * from the previous page is never reported on the next one.
 * @param {{SKU?: string, Category?: string, Subcategory?: string, page_type?: string}|null} product
 * @param {string} [pathname]
 */
export const setExpPageProduct = (product, pathname) => {
    if (typeof window === 'undefined') return
    registered = product ? {...product, pathname: pathname || window.location.pathname} : null
}

/**
 * URL, page_type and the product/category context of the current page.
 * @param {string} [pathname]
 * @returns {{URL: string, page_type: string, Category?: string, Subcategory?: string, SKU?: string}}
 */
export const getExpPageContext = (pathname) => {
    if (typeof window === 'undefined') return {URL: '', page_type: 'other'}
    const path = pathname || window.location.pathname
    const own = registered && registered.pathname === path ? registered : null
    const context = {URL: window.location.href, page_type: own?.page_type || classifyPage(path)}
    const stripped = stripLocale(path)
    const plp = stripped.match(PLP_PATH)
    if (plp) {
        const [Category, Subcategory] = plp[1].split('/').filter(Boolean)
        if (Category) context.Category = Category.toLowerCase()
        if (Subcategory) context.Subcategory = Subcategory.toLowerCase()
    }
    const pdp = stripped.match(PDP_PATH)
    if (pdp) context.SKU = pdp[1]
    if (own) {
        for (const key of ['SKU', 'Category', 'Subcategory']) {
            if (own[key]) context[key] = own[key]
        }
    }
    return context
}
